import React, { useState } from 'react';
import { AlertCircle, ArrowRight, CheckCircle2, ShieldCheck } from 'lucide-react';
import { apiFetch, ApiError } from '../../lib/api';
import { AuthPageLayout } from '../../components/auth/AuthPageLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

type PasswordResetResponse = {
  message: string;
  dev_reset_token?: string;
  dev_reset_url?: string;
};

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = (await apiFetch('/api/v1/auth/password-reset', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      })) as PasswordResetResponse;

      setSubmitted(true);
      setDevResetUrl(response.dev_reset_url || null);
    } catch (err: unknown) {
      if (err instanceof ApiError && typeof err.details?.detail === 'string') {
        setError(err.details.detail);
      } else if (err instanceof Error) {
        setError(err.message || 'Unable to submit password reset request.');
      } else {
        setError('Unable to submit password reset request.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const INPUT =
    'block w-full rounded-lg border border-slate-400 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-[#2fae2c] focus:outline-none focus:ring-2 focus:ring-[#40b93c]/30';

  return (
    <AuthPageLayout showBackToLogin>
      <div className="mb-6 space-y-2">
        <p className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
          <ShieldCheck className="h-3.5 w-3.5" /> Account recovery
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Reset your password</h1>
        <p className="text-sm text-slate-500">We&apos;ll send a secure reset link to your work email.</p>
        <p className="text-xs text-slate-400">For security, we always show the same response whether the account exists or not.</p>
      </div>

      {submitted ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2.5 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700 ring-1 ring-emerald-200">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            If that email exists, a password reset link has been sent.
          </div>
          {devResetUrl && (
            <div className="rounded-lg bg-blue-50 px-4 py-3 text-sm ring-1 ring-blue-200">
              <p className="mb-2 font-medium text-[#1f4778]">Dev shortcut</p>
              <a href={devResetUrl} className="inline-flex items-center gap-1.5 text-[#1f4778] hover:underline">
                Open reset link <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          )}
          <button
            type="button"
            onClick={() => { setSubmitted(false); setDevResetUrl(null); }}
            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            Try a different email
          </button>
          <p className="text-xs text-slate-400">Already signed in? Update your password from Settings &gt; Password.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="flex items-center gap-2.5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
              Email address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={INPUT}
              placeholder="you@company.com"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#40b93c] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#2f962d] hover:shadow-[0_8px_20px_rgba(64,185,60,0.35)] focus:outline-none focus:ring-2 focus:ring-[#40b93c] focus:ring-offset-2 disabled:translate-y-0 disabled:opacity-50"
          >
            {isLoading ? <LoadingSpinner size="sm" /> : 'Send reset link'}
          </button>
        </form>
      )}
    </AuthPageLayout>
  );
};

export default ForgotPasswordPage;
